import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { getOrgTermsContent } from '../../content/utils.js';
import RightArrowIcon from '../icons/RightArrowIcon.jsx';
import SubHeader from '../SubHeader.jsx';
import CategoriesSection from './CategoriesSection.jsx';
import QuestionBlueIcon from '../icons/QuestionBlueIcon.jsx';
import CustomTooltip from '../CustomTooltip.jsx';
import CustomChangeImpactTypesTable from './CustomChangeImpactTypesTable.jsx';
import CustomStatusesTable from './CustomStatusesTable.jsx';
import styles from './AccordionSection.module.css';

const AccordionSection = ({
  type,
  title,
  items,
  max,
  childItems,
  tooltipTitle,
  tooltipContent,
  firstColumn,
  secondColumn,
  checkAuthorizeCustomSettings,
  onShowModal,
  onEdit,
}) => {
  const [categoriesHidden, setCategoriesHidden] = useState(true);

  const toggleInitiatives = useCallback((e) => {
    e.preventDefault();
    setCategoriesHidden((hidden) => !hidden);
  }, []);

  const tableContainerId = `${type}InitiativesTableContainer`;

  const className = classNames(styles.accordionSection, {
    [styles.accordionHidden]: categoriesHidden,
  });

  return (
    <section className={className}>
      <SubHeader flexJustify small accordion>
        <div className={styles.categoriesHeading}>
          <div className="flex row">
            <h2 style={{ marginLeft: '26px' }}>
              <a
                aria-controls={tableContainerId}
                aria-expanded={!categoriesHidden}
                className="flex"
                href="#"
                onClick={toggleInitiatives}
                role="button"
              >
                <RightArrowIcon className="collapse-caret" />
                <span className={`${styles.initiativeType} text-capitalize`}>
                  {title || type}
                </span>
              </a>
            </h2>
            <strong className="bordered text-muted">
              Total:&nbsp;
              <span className="text-info-brand text count">
                {items.length}
                {max ? `/${max}` : ''}
              </span>
            </strong>
            {tooltipContent ? (
              <CustomTooltip
                placement="right"
                tooltip={
                  <div style={{ maxWidth: '256px' }}>
                    <strong>
                      <h5
                        style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          lineHeight: '20px',
                          color: '#153142',
                        }}
                        className="m-b-8"
                      >
                        {tooltipTitle}
                      </h5>
                    </strong>
                    <p
                      style={{
                        fontSize: '12px',
                        fontWeight: '400',
                        lineHeight: '20px',
                        color: '#7F8FA4',
                      }}
                      className="m-b-0 text-muted"
                    >
                      {tooltipContent}
                    </p>
                  </div>
                }
                id="tooltip"
              >
                <Button
                  aria-label="View Support for Change modal"
                  type="button"
                  className="flex flex-align-center m-l-8 btn-last-update-icon"
                >
                  <QuestionBlueIcon />
                </Button>
              </CustomTooltip>
            ) : null}
          </div>
          <div>
            {checkAuthorizeCustomSettings ? (
              <Button bsStyle="default" className="btn-edit" onClick={onEdit}>
                Edit
              </Button>
            ) : null}
          </div>
        </div>
      </SubHeader>
      <div id={tableContainerId}>
        {!categoriesHidden && childItems
          ? childItems.map((category) => (
              <CategoriesSection
                type={category}
                initiativeCategories={items.filter(
                  (i) => !i.archived && i.initiativeType === category
                )}
              />
            ))
          : null}

        {!categoriesHidden &&
          !childItems &&
          type === getOrgTermsContent('projectStatuses') && (
            <CustomStatusesTable
              customStatuses={items}
              initiativeType={type}
              onShowModal={onShowModal}
              firstColumn={firstColumn}
              secondColumn={secondColumn}
            />
          )}

        {!categoriesHidden && !childItems && type === 'Change Impact Types' && (
          <CustomChangeImpactTypesTable
            customStatuses={items}
            initiativeType={type}
            onShowModal={onShowModal}
            firstColumn={firstColumn}
            secondColumn={secondColumn}
          />
        )}
      </div>
    </section>
  );
};

AccordionSection.propTypes = {
  type: PropTypes.string.isRequired,
};

export default AccordionSection;
